import { renderPdfFirstPage } from './pdfThumbnail';

/** Превью локального файла до загрузки: `page` — страница документа (показывается листом). */
export type FileThumbnail =
    { kind: 'image'; src: string } | { kind: 'page'; src: string } | { kind: 'text'; text: string } | { kind: 'none' };

const NONE: FileThumbnail = { kind: 'none' };

/** Ширина растра превью: плитка ~260px × DPR 2. */
const THUMB_WIDTH = 520;
/** Больше — не декодируем: превью не стоит сотен мегабайт в памяти. */
const MAX_IMAGE_BYTES = 50 * 1024 * 1024;
const MAX_SVG_BYTES = 1024 * 1024;
const TEXT_SNIPPET_BYTES = 2048;
const TEXT_SNIPPET_LINES = 14;

const RASTER_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'avif', 'ico']);
const TEXT_EXTS = new Set(['txt', 'csv', 'json', 'xml', 'md', 'log', 'ini', 'yml', 'yaml']);

const extOf = (name: string): string => {
    const dot = name.lastIndexOf('.');
    return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
};

const readAsDataUrl = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
        reader.readAsDataURL(blob);
    });

// webp держит прозрачность PNG; где не поддерживается — браузер отдаст png
const canvasToDataUrl = (canvas: HTMLCanvasElement): string => canvas.toDataURL('image/webp', 0.85);

/** CSP стендов — `img-src 'self' data:`: blob-URL картинки заблокирован, поэтому
 *  растр уменьшаем через createImageBitmap и отдаём data-URL. */
async function imageThumbnail(file: File): Promise<FileThumbnail> {
    if (typeof createImageBitmap !== 'function' || file.size > MAX_IMAGE_BYTES) return NONE;
    const bitmap = await createImageBitmap(file);
    try {
        const scale = Math.min(1, THUMB_WIDTH / bitmap.width);
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(bitmap.width * scale));
        canvas.height = Math.max(1, Math.round(bitmap.height * scale));
        const ctx = canvas.getContext('2d');
        if (!ctx) return NONE;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        return { kind: 'image', src: canvasToDataUrl(canvas) };
    } finally {
        bitmap.close();
    }
}

/** Первые строки текста; не-UTF-8 (выгрузки 1С и т. п.) читаем как windows-1251. */
async function textThumbnail(file: File): Promise<FileThumbnail> {
    const bytes = new Uint8Array(await file.slice(0, TEXT_SNIPPET_BYTES).arrayBuffer());
    let text: string;
    try {
        text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    } catch {
        // обрезка посреди многобайтового символа тоже бросает — проверяем без хвоста
        try {
            text = new TextDecoder('utf-8', { fatal: true }).decode(bytes.subarray(0, bytes.length - 3));
        } catch {
            text = new TextDecoder('windows-1251').decode(bytes);
        }
    }
    const snippet = text
        .replace(/^\uFEFF/, '')
        .split(/\r?\n/)
        .slice(0, TEXT_SNIPPET_LINES)
        .join('\n');
    return snippet.trim() ? { kind: 'text', text: snippet } : NONE;
}

async function buildThumbnail(file: File): Promise<FileThumbnail> {
    const ext = extOf(file.name);
    try {
        if (RASTER_EXTS.has(ext) || (!ext && file.type.startsWith('image/') && file.type !== 'image/svg+xml')) {
            return await imageThumbnail(file);
        }
        if (ext === 'svg' || file.type === 'image/svg+xml') {
            // svg в <img> скрипты не исполняет — data-URL безопасен
            return file.size <= MAX_SVG_BYTES ? { kind: 'image', src: await readAsDataUrl(file) } : NONE;
        }
        if (ext === 'pdf') {
            const canvas = await renderPdfFirstPage(file, THUMB_WIDTH);
            return canvas ? { kind: 'page', src: canvasToDataUrl(canvas) } : NONE;
        }
        if (TEXT_EXTS.has(ext)) return await textThumbnail(file);
    } catch {
        // битый/неподдерживаемый файл (HEIC, повреждённый JPEG, запароленный PDF) — остаётся марка типа
    }
    return NONE;
}

const cache = new WeakMap<File, Promise<FileThumbnail>>();

/** Превью файла; повторные запросы (смена вида, ремаунт плитки) берутся из кэша. */
export function getFileThumbnail(file: File): Promise<FileThumbnail> {
    let pending = cache.get(file);
    if (!pending) {
        pending = buildThumbnail(file);
        cache.set(file, pending);
    }
    return pending;
}
